/**
 * AttachmentPreviewOverlay — the full-screen attachment reader.
 *
 * Shared by AttachmentList (standalone email window) and AttachmentTab (the
 * inline viewer beside an order): the long read lives here, the quick check
 * against the order fields does not (kanban fe62ebea).
 *
 * The close control records as `attachment_preview_close` (kanban bb8e50d3).
 * It was left label-named (`button_close_preview`) while the process-graph
 * lens measured kanban fe62ebea against that node id; that measurement is
 * done, so the family name applies like every other control.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { isPdfAttachment, downloadBlobUrl } from './attachmentPreview';
import styles from './AttachmentPreviewOverlay.module.css';

interface AttachmentPreviewOverlayProps {
  blobUrl: string;
  filename: string;
  mimeType: string;
  /** Email the attachment belongs to; recorded as the target of its controls. */
  emailId?: string;
  onClose: () => void;
}

const AttachmentPreviewOverlay: React.FC<AttachmentPreviewOverlayProps> = ({
  blobUrl,
  filename,
  mimeType,
  emailId,
  onClose,
}) => {
  // Close on Escape (capture so the host modal's own Escape handler does not
  // close the whole email as well).
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [onClose]);

  return createPortal(
    <div
      className={styles.previewOverlay}
      data-testid="attachment-preview-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.previewContainer}>
        <div className={styles.previewHeader}>
          <div className={styles.previewTitle}>{filename}</div>
          <div className={styles.previewActions}>
            <button
              className={styles.previewBtn}
              onClick={() => downloadBlobUrl(blobUrl, filename)}
              type="button"
              data-action="attachment_download"
              data-target-email-id={emailId}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download
            </button>
            <button
              className={styles.previewCloseBtn}
              onClick={onClose}
              type="button"
              aria-label="Close preview"
              data-action="attachment_preview_close"
              data-outcome="close"
              data-target-email-id={emailId}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
        <div className={styles.previewBody}>
          {mimeType.startsWith('image/') ? (
            <img className={styles.previewImage} src={blobUrl} alt={filename} />
          ) : isPdfAttachment(mimeType, filename) ? (
            <iframe
              className={styles.previewIframe}
              src={`${blobUrl}#toolbar=1&navpanes=0&view=FitH`}
              title={filename}
            />
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AttachmentPreviewOverlay;
